export function truncateList(items: string[], limit: number = 1) {
  const front = items.slice(0, limit);
  const back = items.slice(limit);

  if (back.length === 0) {
    if (front.length === 1) {
      return front[0];
    }
    const last = front.pop();
    return [front.join(', '), last].join(' and ');
  }
  return `${front.join(', ')} and ${back.length} more`;
}

const wordPattern = new RegExp(
  ['[A-Z][a-z]+', '[A-Z]+(?=[A-Z][a-z])', '[A-Z]+', '[a-z]+', '[0-9]+'].join(
    '|'
  ),
  'g'
);

/**
 * Capitalizes the first character of a string.
 */
export function upperFirst(string: string): string {
  return string.slice(0, 1).toUpperCase() + string.slice(1);
}

/**
 * Splits a string into its constituent words.
 */
export function words(string = ''): string[] {
  return string.match(wordPattern) || [];
}

export type CamelCase<S extends string> =
  S extends `${infer P1}_${infer P2}${infer P3}`
    ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
    : Lowercase<S>;

/**
 * Camel cases a string. (ex. camelCase)
 */
export function camelCase(string = ''): string {
  return words(string)
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : upperFirst(word.toLowerCase())
    )
    .join('');
}

export function snakeCase(string = ''): string {
  return words(string)
    .map((word) => word.toLowerCase())
    .join('_');
}

/** Email validator, not quite RFC 5322 - would be overkill */
export function validateEmail(email: string) {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

  // return some descriptive error
  if (!email || email.length > 254 || !emailRegex.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address.' };
  }

  const [localPart, domain] = email.split('@');
  if (
    localPart.length > 64 ||
    domain.split('.').some((part) => part.length > 63)
  ) {
    return { isValid: false, message: 'This email address is too long.' };
  }

  return { isValid: true, message: 'This email address is valid.' };
}

/**
 * Validates that an email is valid, and returns the lowercased email
 */
export function sanitizeEmail(email: string) {
  const { isValid, message } = validateEmail(email);
  if (!isValid) throw new Error(message);
  return email.toLowerCase();
}

/** Converts snake case into a title case */
export function convertSnakeToTitle(value: string) {
  return value
    .toLowerCase() // handle screaming
    .split('_')
    .filter((x) => x.length > 0)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
    .join(' ');
}

/* Set default avatar text */
export function defaultAvatarText(name: string | null | undefined) {
  if (name) {
    const nameSplit = name.split(' ');
    if (nameSplit.length === 1) {
      return nameSplit[0].slice(0, 2).toUpperCase();
    } else {
      return `${nameSplit[0][0].toUpperCase()}${nameSplit[nameSplit.length - 1][0].toUpperCase()}`;
    }
  } else {
    return '';
  }
}

/**
 * Validates that a string is a properly formatted URL with http/https protocol
 * @param url - The URL string to validate
 * @returns parsed URL object
 * @throws Error if URL is invalid or uses unsupported protocol
 */
export function validateUrl(url: string) {
  try {
    const parsedUrl = new URL(url);

    // Check for valid protocol
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      throw new Error('URL must use `http` or `https` protocol');
    }

    return parsedUrl;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw error;
  }
}

/**
 * Converts a camelCase or PascalCase string into a human-readable
 * sentence with spaces between words and each word capitalized.
 *
 * Examples:
 *   toSentenceCase("startsWith") => "Starts with"
 *   toSentenceCase("isNotEmpty") => "Is not empty"
 *
 * @param str - The camelCase or PascalCase string to convert.
 * @returns A human-readable sentence form of the input string.
 */
export function convertCamelToSentence(str: string): string {
  return str.replace(/([A-Z])/g, ' $1').toLowerCase();
}

/**
 * Converts a sentence-style string with spaces into camelCase.
 * The first word is kept lowercase, and subsequent words are
 * capitalized and joined without spaces.
 *
 * Examples:
 *   toCamelCase("starts with")  => "startsWith"
 *   toCamelCase("is not empty") => "isNotEmpty"
 *
 * @param str - The sentence-style string to convert.
 * @returns The camelCase version of the input string.
 */
export function convertSentenceToCamel(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, i) =>
      i === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '');
}

/**
 * Normalize admin-entered instance input into a clean bare host (host + optional
 * `:port`). Tolerates a pasted scheme, path, query, or fragment — we only want the
 * host, so the rest is discarded. Rejects embedded credentials (the "a@b" form is
 * ambiguous about which part is the host) and validates the host is a real domain
 * or IPv4 literal.
 *
 * The shared core of every "admin enters a host" REMOTE_URL builder.
 *
 * @param rawHost - The raw host config value the admin entered (may be undefined).
 * @param opts.label - Human name for the field, used in error messages (default "host").
 * @param opts.example - Example host appended to error messages (e.g. "gitlab.com").
 * @returns The validated bare host, including a non-default port if supplied.
 * @throws If the value is missing or doesn't resolve to a valid bare host.
 */
export function extractBareHost(
  rawHost: string | undefined,
  opts?: { label?: string; example?: string }
): string {
  const label = opts?.label ?? 'host';
  const example = opts?.example ? ` (e.g. ${opts.example})` : '';
  const value = rawHost?.trim();
  if (!value) {
    throw new Error(`${label} is required${example}.`);
  }

  const reject = (): never => {
    throw new Error(
      `Invalid ${label}: "${value}". Enter a valid host${example}.`
    );
  };

  // Prepend https:// when no scheme is present so URL() has something to parse.
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return reject();
  }

  // We only care about the host — discard any path, query, or fragment the admin
  // pasted. Still reject embedded credentials, since "a@b" is genuinely ambiguous
  // about which part is the host.
  if (parsed.username || parsed.password) {
    return reject();
  }

  // hostname must be a real-looking host (domain or IP literal), no spaces/junk.
  const host = parsed.hostname;
  const isValidHost =
    /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i.test(
      host
    ) || /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  if (!isValidHost) {
    return reject();
  }

  // Preserve a non-default port if the admin supplied one.
  return parsed.host;
}

/**
 * Turn an admin-entered value into a safe single URL path segment
 *
 * @param rawValue - The raw config value (may be undefined).
 * @param opts.label - Human name for the field, used in error messages (default "value").
 * @returns The trimmed, percent-encoded path segment.
 * @throws If the value is missing, is "." / ".." (which survive encoding and can
 *   normalize into path traversal), or exceeds the length bound.
 */
export function toUrlPathSegment(
  rawValue: string | undefined,
  opts?: { label?: string }
): string {
  const label = opts?.label ?? 'value';
  const value = rawValue?.trim();
  if (!value) {
    throw new Error(`${label} is required.`);
  }
  // "." and ".." survive encodeURIComponent (dots are unreserved) and a URL
  // normalizer could collapse them into path traversal — reject outright.
  if (value === '.' || value === '..') {
    throw new Error(`Invalid ${label}: "${value}".`);
  }
  // Sanity bound — a path segment this long is junk, not a real identifier.
  if (value.length > 255) {
    throw new Error(`${label} must be 255 characters or fewer.`);
  }
  return encodeURIComponent(value);
}
